import cx from 'classnames';
import { icon_sync_partner } from 'components/base/images';
import MuiButton from 'components/common/button/MuiButton';
import CustomSpan from 'components/common/text/CustomSpan';
import T from 'components/common/text/T';
import React, { useMemo } from 'react';
import { useShallowAppSelector } from 'store/hooks';
import { isClinicUserSelector } from 'store/modules/user';
import styled from 'styled-components';
import ProjectFileList from './ProjectFileList';
import ProjectFileViewer from './ProjectFileViewer';

interface ProjectFileProps {
  isCheckedLocal: boolean;
  isCheckedCloud: boolean;
  isRequestedSync: boolean;
  projectInfo: any;
  isSelectedPartner: boolean;
  hasAlarm: any;
  hasViewer: boolean;

  localFileInput: {
    value: any;
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
    reset: () => void;
  };
  localFileInputRef: React.MutableRefObject<any>;
  localFileListCt: any;
  partnerFileList: {
    value: any;
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  cloudFileList: {
    value: any;
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  viewModelFile: {
    value: any;
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  htmlViewerRef: React.MutableRefObject<HTMLIFrameElement | null>;
  viewHtmlFile: {
    value: any;
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  viewImageFile: {
    value: any;
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  syncPartners: {
    email: string;
    groupCode: string;
    isSelf: number;
    name: string;
    userTypeIdx: number | null;
    userGroupIdx: number;
  }[];
  hasMoreSyncPartners: boolean;
  selectedSyncPartner: {
    value: string;
    onChange: (e: any) => void;
    setValue: React.Dispatch<string>;
  };
  checkVisibleModelFile: any;
  isModelFileLoadingProgress: {
    value: boolean;
    onChange: (e: boolean) => void;
    setValue: React.Dispatch<any>;
  };
  selectedModelFile: any;
  onAddLocalFiles: (data: any) => void;
  onClearView: () => void;
  onFetchSyncPartners: () => void;
  onSearchSyncPartners: (value: string) => void;
  onClickFileImport: () => void;
  onClickImageFile: (e: any, id: any, data?: any) => Promise<any>;
  onClickImageFileLocal: (e: any, id: any, data?: any) => Promise<any>;
  onClickViewModelFile: (e: any, id: any, data?: any) => Promise<any>;
  onClickViewModelFileLocal: (e: any, id: any, data?: any) => Promise<any>;
  onClickWireframe: (id: number, data: any) => void;
  onChangeModelOpacity: (e: any, id: number, newValue: number | number[]) => void;
  onClickCheckbox: (e: any, id: any, storage: string, data: any) => void;
  onClickCheckboxAll: (e: any, storage: string, isCheckAll: boolean, shareType: string) => void;
  onClickDeleteFiles: (storage: string, deletelist: any[]) => void;
  onClickDownloadFiles: (id: number, name: string) => Promise<any>;
  onSyncProject: () => void;
  onUploadToPartner: () => void;
  onUploadToGroup: () => void;
  onClickWebViewer: () => void;
}

function ProjectFile({
  isCheckedLocal,
  isCheckedCloud,
  isRequestedSync,
  projectInfo,
  isSelectedPartner,
  hasAlarm,
  hasViewer,
  localFileInput,
  localFileInputRef,
  localFileListCt,
  partnerFileList,
  cloudFileList,
  viewModelFile,
  htmlViewerRef,
  viewHtmlFile,
  viewImageFile,
  syncPartners,
  hasMoreSyncPartners,
  selectedSyncPartner,
  checkVisibleModelFile,
  isModelFileLoadingProgress,
  selectedModelFile,
  onClearView,
  onAddLocalFiles,
  onClickFileImport,
  onFetchSyncPartners,
  onSearchSyncPartners,
  onClickImageFile,
  onClickImageFileLocal,
  onClickViewModelFile,
  onClickViewModelFileLocal,
  onClickWireframe,
  onChangeModelOpacity,
  onClickCheckbox,
  onClickCheckboxAll,
  onClickDeleteFiles,
  onClickDownloadFiles,
  onSyncProject,
  onUploadToPartner,
  onUploadToGroup,
  onClickWebViewer,
}: ProjectFileProps) {
  const { isClinicUser } = useShallowAppSelector(state => ({
    isClinicUser: isClinicUserSelector(state),
  }));

  const defBtnValid = useMemo(
    () => !projectInfo.clientGroupCode && !isClinicUser,
    [projectInfo.clientGroupCode, isClinicUser],
  );

  return (
    <Styled.ProjectFile data-component-name="ProjectFile">
      <div className="projectFile__container">
        <div className="projectFile__section1">
          <ProjectFileList
            projectInfo={projectInfo}
            isSelectedPartner={isSelectedPartner}
            hasAlarm={hasAlarm}
            hasViewer={hasViewer}
            localFileInput={localFileInput}
            localFileInputRef={localFileInputRef}
            localFileListCt={localFileListCt}
            partnerFileList={partnerFileList}
            cloudFileList={cloudFileList}
            onAddLocalFiles={onAddLocalFiles}
            onClickFileImport={onClickFileImport}
            onClearView={onClearView}
            syncPartners={syncPartners}
            hasMoreSyncPartners={hasMoreSyncPartners}
            selectedSyncPartner={selectedSyncPartner}
            onFetchSyncPartners={onFetchSyncPartners}
            onSearchSyncPartners={onSearchSyncPartners}
            onClickImageFile={onClickImageFile}
            onClickImageFileLocal={onClickImageFileLocal}
            onClickViewModelFile={onClickViewModelFile}
            onClickViewModelFileLocal={onClickViewModelFileLocal}
            onClickWireframe={onClickWireframe}
            onChangeModelOpacity={onChangeModelOpacity}
            isModelFileLoadingProgress={isModelFileLoadingProgress}
            selectedModelFile={selectedModelFile}
            onClickCheckbox={onClickCheckbox}
            onClickCheckboxAll={onClickCheckboxAll}
            onClickDeleteFiles={onClickDeleteFiles}
            onClickDownloadFiles={onClickDownloadFiles}
            onClickWebViewer={onClickWebViewer}
          />
        </div>
        <div className="projectFile__section2">
          <ProjectFileViewer
            hasViewer={hasViewer}
            viewModelFile={viewModelFile}
            htmlViewerRef={htmlViewerRef}
            viewHtmlFile={viewHtmlFile}
            viewImageFile={viewImageFile}
            checkVisibleModelFile={checkVisibleModelFile}
            isModelFileLoadingProgress={isModelFileLoadingProgress}
          />
        </div>
      </div>

      <div className="projectFile__button_box">
        {!projectInfo?.partnershipCode ? (
          <MuiButton
            config={{
              color: '#0782ED',
            }}
            disabled={
              !isRequestedSync
                ? !selectedSyncPartner.value
                : defBtnValid
                ? true
                : !isCheckedLocal && !isCheckedCloud
            }
            disableElevation
            variant="contained"
            className="projectUpload__button sync_partner"
            onClick={() => {
              onSyncProject();
            }}
            startIcon={
              <img
                src={icon_sync_partner}
                alt="upload"
                className={cx('projectUpload__button_icon', {
                  isDisabled: !!hasAlarm,
                })}
              />
            }
          >
            <CustomSpan fontSize={16}>
              <T>PROJECT_SYNC_TO_PARTNER</T>
            </CustomSpan>
          </MuiButton>
        ) : (
          <MuiButton
            config={{
              color: '#0782ED',
            }}
            disabled={defBtnValid ? true : !isCheckedLocal && !isCheckedCloud}
            disableElevation
            variant="contained"
            className="projectUpload__button sync"
            onClick={() => {
              onUploadToPartner();
            }}
            startIcon={
              <img
                src={icon_sync_partner}
                alt="upload"
                className={cx('projectUpload__button_icon', {
                  isDisabled: !!hasAlarm,
                })}
              />
            }
          >
            <CustomSpan fontSize={16}>
              <T>PROJECT_SYNC_TO_PARTNER</T>
            </CustomSpan>
          </MuiButton>
        )}
        <MuiButton
          config={{}}
          // disabled={hasAlarm || (!projectInfo.clientGroupCode && !isClinicUser)}
          disabled={!(!hasAlarm && !defBtnValid && !!isCheckedLocal)}
          disableElevation
          variant="contained"
          color="primary"
          className="projectUpload__button group"
          onClick={() => onUploadToGroup()}
          startIcon={
            <img
              src={icon_sync_partner}
              alt="upload"
              className={cx('projectUpload__button_icon', {
                isDisabled: !!hasAlarm,
              })}
            />
          }
        >
          <CustomSpan fontSize={16}>
            <T>PROJECT_SYNC_TO_GROUP</T>
          </CustomSpan>
        </MuiButton>
      </div>
    </Styled.ProjectFile>
  );
}

export default React.memo(ProjectFile);

const Styled = {
  ProjectFile: styled.section`
    position: relative;
    .projectFile__container {
      display: flex;
      box-shadow: 10px 10px 10px rgb(0 0 0 / 16%);
      justify-content: flex-start;
      border-top: none;
      border-radius: 0 0 10px 10px;
      .projectFile__section1 {
        width: 400px;
      }
      .projectFile__section2 {
        width: calc(100% - 400px);
        border-left: 1px solid #e1e1e7;
      }
    }

    .projectFile__button_box {
      width: 100%;
      position: absolute;
      display: flex;
      justify-content: center;
      margin-top: 20px;
      column-gap: 16px;
      .projectUpload__button {
        width: 260px;
        height: 46px;
      }
    }
  `,
};
